import { env } from "@/env"
import { TABLE_LIMIT } from "common/helper/const"
import {
	ItemList,
	PaginatedResult,
	ReportCycle,
	ReportMemberItem,
	ReportSample,
	ReportSampleApplication,
	ReportsDetailItem,
	ReportsItem,
	ReportSubscribe,
} from "common/interface/interface"
import { CycleParams, ReportDetailParams, ReportsParams } from "common/interface/params"
import http from "common/utils/http"

// 是否至少拥有一个英文报告权限
export async function fetchIfUserHasAnyEnReportAuth(auth_token = "") {
	return await http.get<{ auth_token: string }, boolean>("/publicationcenter/if_user_has_any_en_report_auth", {
		params: {
			auth_token,
		},
	})
}
// 事例报告
export async function fetchSampleReports(query: ReportsParams) {
	const { page = 1, limit = TABLE_LIMIT, item_id, cycle, auth_token = "" } = query

	return await http.get<ReportsParams, PaginatedResult<ReportsItem>>("/publicationcenter/get_sample_publication_list", {
		params: {
			page,
			limit,
			item_id,
			cycle,
			auth_token,
		},
	})
}
// 全部报告
export async function fetchReports(query: ReportsParams) {
	const { page = 1, limit = TABLE_LIMIT, item_id, cycle, auth_token = "" } = query

	return await http.get<ReportsParams, PaginatedResult<ReportsItem>>(
		`${env.NEXT_PUBLIC_API_HOST_EN}/publicationcenter/get_publication_list/category`,
		{
			params: {
				page,
				limit,
				item_id,
				cycle,
				IfOnlyCelue: 1, // 过滤年报
				auth_token,
			},
		}
	)
}
// 我的报告
export async function fetchMyReports(query: ReportsParams) {
	const { page = 1, limit = TABLE_LIMIT, item_id, cycle, auth_token = "", Status = 1 } = query

	return await http.get<ReportsParams, PaginatedResult<ReportsItem>>(
		`${env.NEXT_PUBLIC_API_HOST_EN}/publicationcenter/my_reports`,
		{
			params: {
				page,
				limit,
				item_id,
				cycle,
				Status,
				auth_token,
			},
		}
	)
}

/**
 * 获取品目列表   我的
 * @param is_only 是否只获取有权限的品目 1-是 0-否
 */
export async function fetchItemList(auth_token: string, is_only: number = 0) {
	return await http.get<{ auth_token: string; is_only?: number }, { item_list: ItemList[] }>(
		"/publicationcenter/get_all_en_items_order_by_owned",
		{
			params: { auth_token, is_only },
		}
	)
}
/**
 * 获取品目列表   regular
 */
export async function fetchItemListV2(auth_token: string) {
	return await http.get<{ auth_token: string }, { item_list: ItemList[] }>("/publicationcenter/get_all_en_items_v2", {
		params: { auth_token },
	})
}

// 取期刊频率, 有权限的
export async function fetchCycleList(query: Partial<CycleParams>) {
	return await http.get<Partial<CycleParams & { is_check_publication: boolean }>, ReportCycle[]>(
		"/publicationcenter/get_cycle_list",
		{
			params: {
				...query,
				lang: "en",
				is_check_publication: true, // 是否检查是否存在报告
			},
		}
	)
}

// 报告详情
export async function fetchReportsDetail(id: number, auth_token = "") {
	return await http.get<{ id: number; auth_token: string }, ReportsItem>("/publicationcenter/publication_info", {
		params: { id, auth_token },
	})
}
// 获取订阅报告周期
export async function fetchReportsSubscribe(publication_id: number, auth_token: string) {
	return await http.get<{ publication_id: number; auth_token: string }, ReportSubscribe>(
		"/publicationcenter/subscribe_manage_list",
		{
			params: { publication_id, auth_token },
		}
	)
}

// 获取年份下拉框
export async function fetchReportYears(publication_id: number) {
	return await http.get<{ publication_id: number }, string[]>("/publicationcenter/get_report_years", {
		params: { publication_id },
	})
}
// 报告详情(列表)
export async function fetchReportsDetailList(params: ReportDetailParams) {
	return await http.get<ReportDetailParams, PaginatedResult<ReportsDetailItem>>(
		"/publicationcenter/en_publication_child",
		{
			params: { ...params },
		}
	)
}
// 详情推荐  报告列表
export async function fetchRecommendList(auth_token: string, reportId: number) {
	return await http.get<{ auth_token: string; IfOnlyCelue: number; id: number; stop_status: number }, ReportsItem[]>(
		"/publicationcenter/recommend_publication_list",
		{
			params: {
				IfOnlyCelue: 1,
				stop_status: -1, // 停更状态(1查询停更;-1查询未停更)
				id: reportId,
				auth_token,
			},
		}
	)
}

export async function fetchTeamemberList(ids: string) {
	return await http.get<{ member_ids: string }, ReportMemberItem[]>("/publicationcenter/get_team_member", {
		params: { member_ids: ids },
	})
}

// 预览pdf
export const getReportPdfUrl = (params: { pub_child_id: number; auth_token?: string }) => {
	return `${env.NEXT_PUBLIC_API_HOST_EN}/publicationcenter/pdf_data?pub_child_id=${params.pub_child_id}&auth_token=${params.auth_token}`
}

// 下载pdf
export const downloadReportPdfUrl = (params: { pub_child_id: number; auth_token?: string }) => {
	return `${env.NEXT_PUBLIC_API_HOST_EN}/publicationcenter/export_data?pub_child_id=${params.pub_child_id}&auth_token=${params.auth_token}`
}

// 获取报告样本
export function fetchReportSample(publication_id: number, auth_token: string) {
	return http.get<{ publication_id: number; auth_token: string }, ReportSample>("/publicationcenter/report_sample", {
		params: { publication_id, auth_token },
	})
}

// 获取品目列表，兼容数组或 { item_list } 两种响应结构
export async function fetchItemsList() {
	const data = await http.get<null, ItemList[] | { item_list?: ItemList[] }>("/publicationcenter/item_list_v2", {})
	if (!data) return []
	return Array.isArray(data) ? data : (data.item_list ?? [])
}

const normalizeApplySectorList = (items: ItemList[]) =>
	items.filter((item) => item.status !== 2).sort((a, b) => a.sort_number - b.sort_number)

/** 试用申请表单品目列表（SSR） */
export async function fetchApplySectorList() {
	return normalizeApplySectorList(await fetchItemsList())
}

/** 提交报告样本试用申请 */
export function submitReportSampleApplication(data: ReportSampleApplication) {
	return http.post<ReportSampleApplication, null>("/publicationcenter/submit_report_sample_application", {
		data,
		showOriginResponse: true,
	})
}
